import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, imgUrl } from "@/lib/api";
import { MessageCircle } from "lucide-react";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function Messages() {
  const navigate = useNavigate();
  const { data: convos = [], isLoading } = useQuery({
    queryKey: ["conversations"],
    queryFn: async () => (await api.get("/conversations")).data,
    refetchInterval: 4000,
  });

  return (
    <div className="fade-up">
      <h1 className="font-display font-black text-4xl">Messages</h1>
      <p className="text-slate-300 mt-1">Only confirmed plans unlock chat.</p>

      {isLoading ? (
        <div className="space-y-2 mt-6">{[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded-2xl bg-white/5 animate-pulse" />)}</div>
      ) : convos.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center mt-10">
          <MessageCircle className="h-10 w-10 text-amber-400/60 mx-auto mb-3" />
          <p className="text-slate-300">No conversations yet. Pass a vibe check with a mutual yes to start chatting.</p>
        </div>
      ) : (
        <div className="mt-6 glass rounded-2xl overflow-hidden divide-y divide-white/5">
          {convos.map((c) => (
            <button key={c.match_id} data-testid={`convo-${c.match_id}`} onClick={() => navigate(`/chat/${c.match_id}`)}
              className="w-full flex items-center gap-3 p-3.5 hover:bg-white/5 transition-colors text-left">
              <div className="relative shrink-0">
                <img src={imgUrl(c.partner?.photos?.[0] || c.partner?.picture)} alt={c.partner?.name} className="h-14 w-14 rounded-full object-cover" />
                {c.partner?.online && <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-emerald-400 ring-2 ring-[#0B130E]" title="Online now" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold truncate">{c.partner?.name}</h3>
                  <span className="text-[11px] text-slate-400 shrink-0">{timeAgo(c.last_at)}</span>
                </div>
                <p className={`text-sm truncate ${c.unread > 0 ? "text-white font-medium" : "text-slate-300"}`}>
                  {c.last_message || `You matched for ${c.event?.title}`}
                </p>
              </div>
              {c.unread > 0 && <span className="h-5 min-w-[20px] px-1.5 rounded-full bg-pink-500 text-[11px] font-bold text-white flex items-center justify-center shrink-0">{c.unread}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
